import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import AnalyticsProcessWidgets from "components/widgets/analytics-process-widgets/AnalyticsProcessWidgets";
import PageHeader from "components/layout/page-header/PageHeader";
import api from "api";

const CACHE_DURATION = 5 * 60 * 1000;
const PROJECTS_CACHE_DURATION = 5 * 60 * 1000;
// Bump este valor sempre que mudar a estrutura do cache para forçar invalidação
const CACHE_VERSION = "v2";

const Dashboard = ({ sidebarTheme, token, email }) => {
  const [planData, setPlanData] = useState(null);
  const [azureUsageData, setAzureUsageData] = useState(null);
  const [projectsData, setProjectsData] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    const cacheSuffix = (email || "").trim().toLowerCase() || (token ? token.slice(0, 10) : "anon");

    const fetchPrimary = async () => {
      try {
        const cachedPlanData = localStorage.getItem(`cachedPlanData_${CACHE_VERSION}_${cacheSuffix}`);
        const cachedAzureUsageData = localStorage.getItem(`cachedAzureUsageData_${CACHE_VERSION}_${cacheSuffix}`);
        const cachedTimestamp = localStorage.getItem(`cacheTimestamp_${CACHE_VERSION}_${cacheSuffix}`);

        const now = Date.now();
        const cacheAge = cachedTimestamp ? now - Number(cachedTimestamp) : Infinity;

        if (cachedPlanData && cachedAzureUsageData && cacheAge < CACHE_DURATION) {
          setPlanData(JSON.parse(cachedPlanData));
          setAzureUsageData(JSON.parse(cachedAzureUsageData));
          setLoading(false);
          return;
        }

        const [userPlanResponse, azureUsageResponse] = await Promise.all([
          api.get("/plans/user", authHeaders),
          api.get("/plans/usage", authHeaders)
        ]);

        const fetchedPlanData = userPlanResponse.data[0];
        const fetchedAzureUsageData = azureUsageResponse.data;

        localStorage.setItem(`cachedPlanData_${CACHE_VERSION}_${cacheSuffix}`, JSON.stringify(fetchedPlanData));
        localStorage.setItem(`cachedAzureUsageData_${CACHE_VERSION}_${cacheSuffix}`, JSON.stringify(fetchedAzureUsageData));
        localStorage.setItem(`cacheTimestamp_${CACHE_VERSION}_${cacheSuffix}`, now);

        setPlanData(fetchedPlanData);
        setAzureUsageData(fetchedAzureUsageData);
        setLoading(false);
      } catch (error) {
        console.error("Erro ao buscar dados do plano:", error);
        setLoading(false);
      }
    };

    const fetchProjects = async () => {
      try {
        const cached = localStorage.getItem(`cachedProjectsData_${CACHE_VERSION}_${cacheSuffix}`);
        const ts = localStorage.getItem(`projectsCacheTimestamp_${CACHE_VERSION}_${cacheSuffix}`);
        if (cached && ts && Date.now() - Number(ts) < PROJECTS_CACHE_DURATION) {
          const parsed = JSON.parse(cached);
          const temAlgo =
            parsed &&
            typeof parsed === "object" &&
            Object.keys(parsed).some(k => Array.isArray(parsed[k]) && parsed[k].length > 0);
          if (temAlgo) {
            console.log("[v4d:Dashboard] projetos — cache local (sem refetch)", {
              cacheSuffix,
              email,
              keysNormalizadas: parsed && typeof parsed === "object" ? Object.keys(parsed) : null,
              amostra: parsed,
            });
            setProjectsData(parsed);
            return;
          }
        }

        const res = await api.get("/projects/user", authHeaders);
        const raw = res.data;
        console.log("[v4d:Dashboard] GET /projects/user", {
          status: res.status,
          email,
          cacheSuffix,
          tipo: raw === null ? "null" : Array.isArray(raw) ? "array" : typeof raw,
          tamanhoOuKeys: Array.isArray(raw)
            ? { length: raw.length }
            : raw && typeof raw === "object"
              ? { keys: Object.keys(raw) }
              : raw,
          payloadBruto: raw,
        });

        // Nova estrutura da API: array de projetos
        // [{ name, thumbnail, last_date, date_count, dates: [{ date, products, gemeo_status, gemeo_progress }] }]
        // Normaliza para: { [product]: [{ name, date, thumbnail, conversor? }] }
        const normalized = {};

        if (Array.isArray(raw)) {
          for (const project of raw) {
            const { name, thumbnail, dates = [] } = project;
            for (const dateEntry of dates) {
              const { date, products = [], gemeo_status, gemeo_progress } = dateEntry;
              for (const product of products) {
                if (!normalized[product]) normalized[product] = [];
                const entry = { name, date, thumbnail: thumbnail || null };
                if (product === "gemeo_digital" && gemeo_status) {
                  entry.conversor = { status: gemeo_status, progress: gemeo_progress };
                }
                normalized[product].push(entry);
              }
            }
          }
          for (const type of Object.keys(normalized)) {
            normalized[type].sort((a, b) => new Date(b.date) - new Date(a.date));
          }
        } else if (raw && typeof raw === "object") {
          // Formato legado — compatibilidade
          Object.entries(raw).forEach(([type, projectsByName]) => {
            if (!projectsByName || typeof projectsByName !== "object") return;
            normalized[type] = [];
            Object.entries(projectsByName).forEach(([name, value]) => {
              if (!Array.isArray(value)) return;
              value.forEach(item => {
                if (typeof item === "string") {
                  normalized[type].push({ name, date: item });
                } else if (item && typeof item === "object") {
                  normalized[type].push({ name, date: item.date, conversor: item });
                }
              });
            });
          });
        }

        localStorage.setItem(`cachedProjectsData_${CACHE_VERSION}_${cacheSuffix}`, JSON.stringify(normalized));
        localStorage.setItem(`projectsCacheTimestamp_${CACHE_VERSION}_${cacheSuffix}`, Date.now());
        console.log("[v4d:Dashboard] projetos normalizados (widgets)", {
          keysPorProduto: Object.keys(normalized),
          contagens: Object.fromEntries(
            Object.entries(normalized).map(([k, v]) => [k, Array.isArray(v) ? v.length : 0])
          ),
          normalized,
        });
        setProjectsData(normalized);
      } catch (err) {
        console.error("Erro ao buscar projetos:", err);
        console.log("[v4d:Dashboard] erro em /projects/user", {
          message: err?.message,
          status: err?.response?.status,
          data: err?.response?.data,
        });
      }
    };

    const fetchActivity = async () => {
      try {
        const res = await api.get("/logs/activity-logs?limit=8", authHeaders);
        const raw = res.data;
        const logs = Array.isArray(raw) ? raw : raw?.logs || raw?.data || [];
        setActivityData(logs);
      } catch (err) {
        console.error("Erro ao buscar atividade:", err);
      }
    };

    if (token) {
      fetchPrimary();
      fetchProjects();
      fetchActivity();
    }
  }, [token]);

  if (loading) {
    return (
      <div className="h-100">
        <PageHeader
          title="Dashboard"
          subtitle="Monitore as métricas dos seus projetos e a memória disponível."
          accent="Visão geral"
        />
        <div className="d-flex justify-content-center align-items-center">
          <div className="spinner-border ms-3" role="status" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-100">
      <PageHeader
        title="Dashboard"
        subtitle="Monitore as métricas dos seus projetos e a memória disponível."
        accent="Visão geral"
      />

      <div className="row ma-0 mt-4">
        <div className="d-flex flex-column w-100">
          <div className="mx-2">
            <AnalyticsProcessWidgets
              planData={planData}
              azureUsageData={azureUsageData}
              projectsData={projectsData}
              activityData={activityData}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  ...state.themeChanger,
  token: state.auth.accessToken,
  email: state.auth.email,
});

export default connect(mapStateToProps)(Dashboard);
